/* eslint-disable react-refresh/only-export-components -- Tiptap extension module: exports a node + helper, not an HMR component boundary. */
import { Node, mergeAttributes } from '@tiptap/core'
import {
  ReactNodeViewRenderer,
  NodeViewWrapper,
  type ReactNodeViewProps,
} from '@tiptap/react'
import { useTranslation } from 'react-i18next'

// Fallback label for a token (client_name -> "Client name") when no localized
// label exists in the `settings:templates.tokens` map.
export function humanizeToken(token: string): string {
  const s = token.replace(/_/g, ' ').trim()
  return s.charAt(0).toUpperCase() + s.slice(1)
}

// The visual chip shown inside the editor for an inserted placeholder. It is a
// non-editable atom (AC03) — users see a localized "Client name" / "Nom du
// client", not `{{client_name}}`. The label follows the active language via the
// same `settings:templates.tokens` map used by the placeholder palette; the
// stored HTML keeps the raw `data-placeholder="token"`, so this is display-only.
function PlaceholderChip({ node }: ReactNodeViewProps) {
  const { t } = useTranslation('settings')
  const token = String(node.attrs.token ?? '')
  const label = t(`templates.tokens.${token}`, {
    defaultValue: humanizeToken(token),
  })
  return (
    <NodeViewWrapper
      as='span'
      className='mx-0.5 inline-flex max-w-full min-w-0 select-none items-center rounded-md border border-[#ffd8c2] bg-[#fff1ea] px-2 py-0.5 align-baseline text-xs font-medium break-words text-[#d9480f] whitespace-normal [box-sizing:border-box]'
      contentEditable={false}
      data-token={token}
    >
      {label}
    </NodeViewWrapper>
  )
}

/**
 * Tiptap inline atom node for a template placeholder token (2.11.14-2.11.18).
 * Round-trips to/from the stored `{{token}}` HTML via the editor's
 * toStorage/fromStorage helpers; in the DOM/getHTML it serializes to
 * `<span data-placeholder="token"></span>`.
 */
export const PlaceholderNode = Node.create({
  name: 'placeholder',
  group: 'inline',
  inline: true,
  atom: true,
  selectable: true,

  addAttributes() {
    return {
      token: {
        default: '',
        parseHTML: el => el.getAttribute('data-placeholder') ?? '',
        renderHTML: attrs => ({ 'data-placeholder': attrs.token }),
      },
    }
  },

  parseHTML() {
    return [{ tag: 'span[data-placeholder]' }]
  },

  renderHTML({ HTMLAttributes }) {
    return ['span', mergeAttributes(HTMLAttributes)]
  },

  addNodeView() {
    return ReactNodeViewRenderer(PlaceholderChip)
  },
})
